import { IconPlus } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { QueryBoundary } from '../components/QueryBoundary';
import { Skeleton } from '../components/Skeleton';
import { TemplateFormModal } from '../components/TemplateFormModal';
import { TemplateTable } from '../components/TemplateTable';
import { Toast } from '../components/Toast';
import { Button } from '../components/ui/Button';
import { t } from '../i18n';
import { createAgentConfigEditor } from '../lib/agent-config-editor';
import {
  type ApiTemplate,
  deleteTemplate,
  saveTemplate,
  type TemplateWrite,
  useTemplates,
} from '../lib/agent-templates';
import { SESSION_TOKEN } from '../lib/session';

// Agent templates (#288) — the human door onto a capability that had a REST
// route (`/v1/templates`) and an MCP verb (`template`) but no screen. CONTAINER:
// it owns the read, hands rows to <TemplateTable/> and writes to the editor.
//
// Every write is followed by a RE-READ, never a patch from the write's echo —
// the API's row is the answer (it re-parses what it committed), and a template
// that failed its stored-shape check must not appear here as if it had landed.
//
// No Load-more: `GET /v1/templates` serves the whole collection deliberately
// (see `lib/agent-templates.ts`), so there is no cursor to page and a button
// promising one would be a lie.
//
// No client-side role gate. `write:runners` is admin+owner, but the neighbouring
// `write:runners` surface (Fleet's revoke) does not mirror it either, and the
// server is the authority: a refused write renders the API's own 403 inline
// rather than a control that silently is not there.

export function AccountTemplates(props: { token?: string | null } = {}): JSX.Element {
  // `=== undefined`, not `??`: an injected `null` MEANS "no session" and must
  // survive, or the signed-out branch can't be driven from a dev build.
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const query = useTemplates(token);
  const editor = createAgentConfigEditor<ApiTemplate>({
    fallback: t('account_templates.write_failed'),
    onWritten: () => void query.refetch(),
  });

  const onSave = (name: string, body: TemplateWrite): void =>
    editor.submit(() => saveTemplate(name, body, token));

  return (
    <section class="page account-templates">
      <PageHeader
        title={t('account_templates.title')}
        desc={t('account_templates.subtitle')}
        actions={
          <Show when={token !== null}>
            <Button onClick={() => editor.open()}>
              <IconPlus />
              {t('account_templates.add')}
            </Button>
          </Show>
        }
      />

      <Show
        when={token !== null}
        fallback={<EmptyState message={t('account_templates.no_session')} />}
      >
        {/* QueryBoundary, not a bare Show: loading must not read as "empty". */}
        <QueryBoundary
          query={query}
          isEmpty={(rows) => rows.length === 0}
          loading={<Skeleton height="14rem" />}
          empty={
            <EmptyState
              message={t('account_templates.empty')}
              ctaLabel={t('account_templates.add')}
              onCta={() => editor.open()}
            />
          }
        >
          {(rows) => (
            <Panel title={t('account_templates.list_title')}>
              <TemplateTable rows={rows} onEdit={editor.open} onDelete={editor.askDelete} />
            </Panel>
          )}
        </QueryBoundary>
      </Show>

      <TemplateFormModal
        target={editor.editing()}
        pending={editor.pending()}
        error={editor.error()}
        onSave={onSave}
        onClose={editor.close}
      />

      <ConfirmDialog
        open={editor.deleting() !== null}
        title={t('account_templates.delete_title', { name: editor.deleting()?.name ?? '' })}
        desc={t('account_templates.delete_desc')}
        cancelLabel={t('account_templates.cancel')}
        actionLabel={t('account_templates.delete')}
        destructive
        onCancel={editor.cancelDelete}
        onConfirm={() => editor.confirmDelete((template) => deleteTemplate(template.name, token))}
      />
      <Toast message={editor.deleteError()} tone="error" onDismiss={editor.clearDeleteError} />
    </section>
  );
}
